/**
 * #218 — Cross-subdomain auth handoff via URL fragment.
 *
 * When a tenant user signs in on the central root (`console.{baseDomain}`) or
 * on the wrong tenant subdomain, the frontend redirects them to the correct
 * host. localStorage is origin-scoped, so the Cognito tokens minted on the
 * source host are NOT visible on the destination host — the user would land
 * on the login form again and have to re-type.
 *
 * The Cognito triplet (IdToken + AccessToken + RefreshToken) is ~4KB raw, so
 * a parent-domain cookie (`Domain=.gisify.dev`) silently exceeds the 4KB
 * per-cookie browser limit. URL fragments don't have that limit and never
 * traverse the network — same shape as the OAuth implicit flow's response
 * mode. The destination page reads `location.hash`, decodes, hydrates
 * localStorage, and clears the hash via `history.replaceState` so the tokens
 * never appear in `document.referrer`, navigation history snapshots, or
 * shared URLs after the first paint.
 *
 * A hand-off MOVES the session; it never copies it. Every redirect that
 * carries tokens goes through {@link handOffTo}, which also makes the SOURCE
 * origin forget them. When the root kept its copy, signing out on the tenant
 * subdomain (which clears only that origin) did not stick: the next visit to
 * the root found the old session and handed it over again, signing the user
 * straight back in.
 */

import { rotateAnonymousId } from './anonymous-id'

const FRAGMENT_KEY = 'gisify_handoff'

const TOKEN_KEY = 'gisify_token'
const ACCESS_TOKEN_KEY = 'gisify_access_token'
const REFRESH_TOKEN_KEY = 'gisify_refresh_token'
const USER_KEY = 'gisify_user'

/**
 * Every key that holds the session on an origin: the three tokens plus the
 * cached profile `useAuth` renders "signed in" from. The same four keys
 * `clearStoredTokens()` in `./index.ts` removes (both auth clients persist
 * only the access + refresh tokens). That barrel is not imported here because
 * it pulls in the Cognito SDK; keep the two lists in step.
 */
const SESSION_KEYS = [TOKEN_KEY, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY] as const

export interface HandoffPayload {
  idToken: string
  accessToken?: string
  refreshToken?: string
}

/**
 * Append a base64-encoded handoff payload to the given URL's fragment. Caller
 * is expected to immediately navigate to the resulting URL.
 */
export function appendHandoffToUrl(url: URL, payload: HandoffPayload): URL {
  const encoded = btoa(JSON.stringify(payload))
  url.hash = `${FRAGMENT_KEY}=${encoded}`

  return url
}

/**
 * Parse a handoff payload from the current URL's fragment. Returns null when
 * no fragment is present or decoding fails. Does NOT mutate the URL.
 */
export function readHandoffFromUrl(): HandoffPayload | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  if (!hash) return null
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(trimmed)
  const encoded = params.get(FRAGMENT_KEY)
  if (!encoded) return null
  try {
    return JSON.parse(atob(encoded)) as HandoffPayload
  } catch {
    return null
  }
}

/**
 * Strip the handoff fragment from the current URL via `history.replaceState`
 * so the tokens are removed from `document.referrer`, navigation snapshots,
 * and any URL the user might share. Idempotent.
 */
export function clearHandoffFromUrl(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)

  if (url.hash.includes(FRAGMENT_KEY)) {
    url.hash = ''
    window.history.replaceState(null, '', url.toString())
  }
}

/**
 * If a handoff payload exists in the URL fragment, hydrate localStorage from
 * it and strip the fragment. Returns true when consumed (caller should re-run
 * auth bootstrap).
 */
export function consumeHandoffIntoLocalStorage(): boolean {
  const payload = readHandoffFromUrl()
  if (!payload) return false
  if (payload.idToken) localStorage.setItem(TOKEN_KEY, payload.idToken)
  if (payload.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, payload.accessToken)
  if (payload.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken)
  clearHandoffFromUrl()

  return true
}

/**
 * Snapshot the in-memory tokens that should travel with a redirect. Returns
 * null when there's no idToken (signed-out state — nothing to hand off).
 */
export function readTokensForHandoff(): HandoffPayload | null {
  if (typeof localStorage === 'undefined') return null
  const idToken = localStorage.getItem(TOKEN_KEY)
  if (!idToken) return null

  return {
    idToken,
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY) ?? undefined,
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined,
  }
}

/**
 * Move this origin's session to `url` (another console host) and navigate
 * there. In order: the tokens go into the URL fragment, this origin's session
 * keys are removed, the anonymous id is rotated, then the page is replaced.
 *
 * This deliberately does NOT call the provider's sign-out (Cognito
 * GlobalSignOut): that would revoke the refresh token being handed over and
 * the destination would land signed out.
 *
 * The anonymous id is rotated for the reason `signOut` rotates it: the API
 * Gateway authorizer caches its decision per `X-Anonymous-Identity` for ~5
 * minutes, so an anonymous request from this origin could otherwise be served
 * the signed-in decision of the session that just left.
 */
export function handOffTo(url: URL): void {
  const tokens = readTokensForHandoff()
  if (tokens) appendHandoffToUrl(url, tokens)
  for (const key of SESSION_KEYS) localStorage.removeItem(key)
  rotateAnonymousId()
  window.location.replace(url.toString())
}
